<template>
  <!-- drawerContents はメタデータのみ。リッチテキスト内の .drawer-trigger 経由でドロワー表示 -->
  <template v-if="content.fieldId === 'drawerContents'" />

  <template v-else-if="content.fieldId === 'richTextEditor'">
    <div :class="{ 'pl-4': forItinerary }">
      <StyledRichText :html="content.richTextEditor" />
    </div>
  </template>

  <template v-else-if="content.fieldId === 'mediumExps'">
    <div :class="forItinerary ? 'pl-4 pr-1' : 'first:mt-3'">
      <template v-for="item in resolvedMediumExps" :key="item.microCMSExperience.id">
        <MediumExperienceCard :experience="item.experience" :micro-c-m-s-experience="item.microCMSExperience" :theme="'light'" />
      </template>
    </div>
  </template>

  <template v-else-if="content.fieldId === 'smallExps'">
    <div class="py-4" :class="forItinerary ? 'pl-4 pr-1' : ''">
      <SmallExperiencesSection :experiences="resolvedSmallExperiences" />
    </div>
  </template>

  <template v-else-if="content.fieldId === 'stories'">
    <div class="flex flex-col gap-4 py-4" :class="forItinerary ? 'pl-4 pr-1' : ''">
      <template v-for="story in content.stories" :key="story.id">
        <StoryWideCard :story="story" />
      </template>
    </div>
  </template>

  <template v-else-if="content.fieldId === 'ctaButton'">
    <div class="py-4 md:mx-auto md:max-w-[368px]" :class="forItinerary && 'pl-4 pr-1'">
      <AtomsPrimaryButton :on-click="onClickCtaButton">{{ content.label }} </AtomsPrimaryButton>
    </div>
  </template>

  <template v-else-if="content.fieldId === 'largeExps' && experience && cmsExperience">
    <div class="my-8 break-keep">
      <ExperienceInfoSection :cms-experience="cmsExperience" :experience="experience" />
    </div>
  </template>

  <template v-else-if="content.fieldId === 'imageMedia'">
    <div
      :class="
        forItinerary ? 'pl-4 pr-1' : ['py-4', isSP ? '-mx-4 w-screen' : 'relative left-1/2 w-[90vw] md:-translate-x-1/2', multiColumnLayout ? 'md:w-full' : 'md:w-[calc(100vw-2rem)] md:max-w-[1136px]']
      "
    >
      <ImageMediaSection :multi-column-layout="multiColumnLayout" :pc-images="pcImageUrls" :sp-images="spImageUrls" />
    </div>
  </template>

  <template v-else-if="content.fieldId === 'videoMedia'">
    <div
      :class="
        forItinerary ? 'pl-4 pr-1' : ['py-4', isSP ? '-mx-4 w-screen' : 'relative left-1/2 w-[90vw] md:-translate-x-1/2', multiColumnLayout ? 'md:w-full' : 'md:w-[calc(100vw-2rem)] md:max-w-[1136px]']
      "
    >
      <VideoMediaSection :video-url="videoUrl" />
    </div>
  </template>
</template>

<script setup lang="ts">
import type { EnExperienceViewModel } from '@/models/enExperience';
import { useIsSP } from '@/wabunka-src/common/composables';
import { findExperienceByMicroCMSExperience, filterExperiencesByMicroCMSExperiences, type CustomPart, StyledRichText, ExperienceInfoSection, ImageMediaSection } from '@/wabunka-src/common/features';
import VideoMediaSection from '@/wabunka-src/common/features/Featured/components/VideoMediaSection/VideoMediaSection.vue';
import type { DrawerContentItem } from '@/wabunka-src/common/features/Featured/models/featured/featured';
import { extractImageUrls } from '@/wabunka-src/common/utils';
import { type ExperienceViewModel, type ExperienceListViewModel, MediumExperienceCard, SmallExperiencesSection } from '@/wabunka-src/features/Experience';
import { StoryWideCard } from '@/wabunka-src/features/Story';

interface Props {
  content: CustomPart | { fieldId: 'drawerContents'; drawerContents: DrawerContentItem[] };
  experiences: ExperienceListViewModel;
  /**
   * Itinerary用にスタイルを調整するかどうか
   */
  forItinerary?: boolean;
  multiColumnLayout?: boolean;
  /**
   * CTA ボタンクリック時に呼ばれる。true を返すと遷移（navigateTo）を行わない。
   * 親でリンク先に応じた独自処理（例: ドロワーを開く）を行いたい場合に使用する。
   */
  onCtaLinkClick?: (link: string) => boolean;
}

const props = withDefaults(defineProps<Props>(), {
  forItinerary: false,
  onCtaLinkClick: undefined,
});
const isSP = useIsSP();

const onClickCtaButton = () => {
  if (props.content.fieldId !== 'ctaButton') {
    return;
  }

  const link = props.content.link;
  if (props.onCtaLinkClick?.(link)) {
    return;
  }

  navigateTo(link, {
    external: false,
    open: { target: '_self' },
  });
};

const cmsExperience = computed<EnExperienceViewModel | undefined>(() => (props.content.fieldId === 'largeExps' ? props.content.experience : undefined));
const experience = computed<ExperienceViewModel | undefined>(() => (cmsExperience.value ? findExperienceByMicroCMSExperience(props.experiences, cmsExperience.value) : undefined));

const resolvedMediumExps = computed(() => {
  if (props.content.fieldId !== 'mediumExps') return [];
  return props.content.experiences.map((microCMSExperience) => ({
    microCMSExperience,
    experience: findExperienceByMicroCMSExperience(props.experiences, microCMSExperience),
  }));
});

const resolvedSmallExperiences = computed(() => {
  if (props.content.fieldId !== 'smallExps') return [];
  return filterExperiencesByMicroCMSExperiences(props.experiences, props.content.experiences);
});
// SP/PC を両方渡し、ImageMediaSection 側で picture により出し分ける（JS の isSP で src を切り替えない）。
const pcImageUrls = computed(() => (props.content.fieldId === 'imageMedia' ? extractImageUrls(props.content.pcImageMedias) : []));
const spImageUrls = computed(() => (props.content.fieldId === 'imageMedia' ? extractImageUrls(props.content.spImageMedias) : []));
const videoUrl = computed(() => {
  if (props.content.fieldId !== 'videoMedia') return '';
  const videoMedia = isSP.value ? props.content.spVideoMedia : props.content.pcVideoMedia;
  return videoMedia?.url ?? '';
});
</script>
